import { useCallback, useEffect, useRef, useState } from 'react';
import { erApiFeil, erNettfeil } from '../api/feil.ts';
import { useHentHypotesePaaNytt, useLagreUtkast, useOpprettUtkast, type Hypothesis } from '../api/useHypotheses.ts';
import { forLange, fraHypotese, tilKropp, tilPatch, TOMT, ulikeFelt, type Felt, type Skjema } from './skjema.ts';

/**
 * Utkastet fra første tastetrykk (H2.2; `hypotese-innsending.jsx`, SKJEMAET):
 *
 * - Ingenting lagres før første tastetrykk. Da oppretter `POST /hypotheses` utkastet.
 * - Autolagring er `PATCH` med `baseVersion`, tidligst `VENT_MS` etter siste tastetrykk og
 *   straks ved hvert ledd-bytte. «Lagret 10:12» er tjenerens svar, aldri nettleserens lager.
 * - Ett kall ute om gangen. Det som skrives mens et kall er ute, lagres i neste.
 * - Uten nett står teksten i minnet (denne hooken) til fanen lukkes, og lagres når nettet er
 *   tilbake (`online`). Lukkes fanen med ulagret tekst, spør nettleseren først. Ingen
 *   `localStorage` (K8, lint-vakt 1).
 * - 409 `draft_changed_elsewhere` (samme utkast på mobil og PC): tjenerens versjon hentes, og
 *   feltene som er ulike legges fram. Ingenting skrives før personen har valgt.
 * - Et felt over kontraktens maksgrense stopper lagringen; teksten står.
 */

export const VENT_MS = 1000;

export type Lagretilstand = 'ingen' | 'lagrer' | 'lagret' | 'nett' | 'konflikt' | 'forLang' | 'feil';

export interface Konflikt {
  server: Hypothesis;
  felt: Felt[];
}

const klokke = () => new Intl.DateTimeFormat('nb-NO', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Oslo' }).format(new Date());

export function useUtkastLagring({ onOpprettet }: { onOpprettet: (id: string) => void }) {
  const opprett = useOpprettUtkast();
  const lagre = useLagreUtkast();
  const hentPaaNytt = useHentHypotesePaaNytt();

  const [skjema, setSkjema] = useState<Skjema>(TOMT);
  const [id, setId] = useState<string | null>(null);
  const [tilstand, setTilstandState] = useState<Lagretilstand>('ingen');
  const [lagretKl, setLagretKl] = useState<string | null>(null);
  const [konflikt, setKonflikt] = useState<Konflikt | null>(null);
  const [feiltekst, setFeiltekst] = useState<string | null>(null);

  // Den levende tilstanden for kallene: en timer eller en `online`-lytter skal aldri lese en
  // gammel lukning. React-tilstanden over er det skjermen tegner.
  const r = useRef({
    skjema: TOMT,
    id: null as string | null,
    versjon: null as number | null,
    endring: 0,
    lagretEndring: 0,
    paagaar: null as Promise<boolean> | null,
    konflikt: false,
    tilstand: 'ingen' as Lagretilstand,
    timer: undefined as ReturnType<typeof setTimeout> | undefined,
  });
  const opprettetRef = useRef(onOpprettet);
  opprettetRef.current = onOpprettet;

  const setTilstand = useCallback((t: Lagretilstand) => {
    r.current.tilstand = t;
    setTilstandState(t);
  }, []);

  const lagreNaa = useCallback(async (): Promise<boolean> => {
    const s = r.current;
    clearTimeout(s.timer);
    while (s.paagaar) await s.paagaar;
    if (s.endring === s.lagretEndring) return s.id !== null || s.endring === 0;
    if (s.konflikt) return false;
    if (forLange(s.skjema).length > 0) {
      setTilstand('forLang');
      return false;
    }

    const endring = s.endring;
    const skjemaNaa = s.skjema;
    setTilstand('lagrer');
    const jobb = (async (): Promise<boolean> => {
      try {
        const ny = s.id === null;
        // PATCH er merge-patch: et tømt tall, en tom dato eller et fjernet valg går som null.
        const h = ny
          ? await opprett.mutateAsync(tilKropp(skjemaNaa))
          : await lagre.mutateAsync({ id: s.id!, body: { ...tilPatch(skjemaNaa), baseVersion: s.versjon! } });
        s.id = h.id;
        s.versjon = h.version;
        s.lagretEndring = endring;
        if (ny) {
          setId(h.id);
          opprettetRef.current(h.id);
        }
        setLagretKl(klokke());
        setFeiltekst(null);
        setTilstand(s.endring === endring ? 'lagret' : 'lagrer');
        return true;
      } catch (e) {
        if (erNettfeil(e)) {
          setTilstand('nett');
          return false;
        }
        if (erApiFeil(e) && e.code === 'draft_changed_elsewhere' && s.id) {
          try {
            const server = await hentPaaNytt(s.id);
            const felt = ulikeFelt(s.skjema, fraHypotese(server));
            if (felt.length === 0) {
              // Den andre enheten lagret det samme: ingenting å velge mellom.
              s.versjon = server.version;
              s.lagretEndring = endring;
              setLagretKl(klokke());
              setTilstand('lagret');
              return true;
            }
            s.konflikt = true;
            setKonflikt({ server, felt });
            setTilstand('konflikt');
            return false;
          } catch (e2) {
            setTilstand(erNettfeil(e2) ? 'nett' : 'feil');
            if (!erNettfeil(e2)) setFeiltekst(erApiFeil(e2) ? e2.brukertekst : 'Utkastet ble ikke lagret. Prøv igjen.');
            return false;
          }
        }
        setFeiltekst(erApiFeil(e) ? e.brukertekst : 'Utkastet ble ikke lagret. Prøv igjen.');
        setTilstand('feil');
        return false;
      }
    })();
    s.paagaar = jobb;
    const ok = await jobb;
    s.paagaar = null;
    // Skrevet mens kallet var ute: lagre det også, før svaret gis videre.
    if (ok && s.endring !== s.lagretEndring) return lagreNaa();
    return ok;
  }, [opprett, lagre, hentPaaNytt, setTilstand]);

  const lagreRef = useRef(lagreNaa);
  lagreRef.current = lagreNaa;

  const oppdater = useCallback((patch: Partial<Skjema>) => {
    const s = r.current;
    s.skjema = { ...s.skjema, ...patch };
    s.endring += 1;
    setSkjema(s.skjema);
    clearTimeout(s.timer);
    s.timer = setTimeout(() => void lagreRef.current(), VENT_MS);
  }, []);

  /** Utkastet fra tjeneren: ved åpning fra en lenke, eller på den andre enheten. */
  const last = useCallback(
    (h: Hypothesis) => {
      const s = r.current;
      clearTimeout(s.timer);
      s.id = h.id;
      s.versjon = h.version;
      s.skjema = fraHypotese(h);
      s.endring = 0;
      s.lagretEndring = 0;
      s.konflikt = false;
      setSkjema(s.skjema);
      setId(h.id);
      setKonflikt(null);
      setTilstand('ingen');
    },
    [setTilstand],
  );

  /** «Ny hypotese» fra et åpent utkast: et nytt, tomt skjema. Det gamle er lagret først. */
  const nullstill = useCallback(() => {
    const s = r.current;
    clearTimeout(s.timer);
    Object.assign(s, { skjema: TOMT, id: null, versjon: null, endring: 0, lagretEndring: 0, konflikt: false });
    setSkjema(TOMT);
    setId(null);
    setKonflikt(null);
    setLagretKl(null);
    setTilstand('ingen');
  }, [setTilstand]);

  /** Konflikten: tjenerens versjon gjelder. Skjemaet blir den, og ingenting sendes. */
  const brukLagret = useCallback(() => {
    const s = r.current;
    if (!konflikt) return;
    s.skjema = fraHypotese(konflikt.server);
    s.versjon = konflikt.server.version;
    s.konflikt = false;
    s.lagretEndring = s.endring;
    setSkjema(s.skjema);
    setKonflikt(null);
    setLagretKl(klokke());
    setTilstand('lagret');
  }, [konflikt, setTilstand]);

  /** Konflikten: denne versjonen gjelder. Sendes med tjenerens nye versjon som grunnlag. */
  const beholdDenne = useCallback(() => {
    const s = r.current;
    if (!konflikt) return;
    s.versjon = konflikt.server.version;
    s.konflikt = false;
    s.endring += 1;
    setKonflikt(null);
    void lagreRef.current();
  }, [konflikt]);

  // Nettet er tilbake: det som står i minnet, lagres straks.
  useEffect(() => {
    const paaNett = () => {
      if (r.current.tilstand === 'nett') void lagreRef.current();
    };
    window.addEventListener('online', paaNett);
    return () => window.removeEventListener('online', paaNett);
  }, []);

  // Lukkes fanen med ulagret tekst, spør nettleseren først (teksten finnes bare her).
  useEffect(() => {
    const foerLukk = (e: BeforeUnloadEvent) => {
      if (r.current.endring === r.current.lagretEndring) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', foerLukk);
    return () => window.removeEventListener('beforeunload', foerLukk);
  }, []);

  // Går personen fra skjemaet i panelet (sidemenyen, tilbake), lagres det som står.
  useEffect(() => {
    const s = r.current;
    return () => {
      clearTimeout(s.timer);
      if (s.endring !== s.lagretEndring && !s.konflikt) void lagreRef.current();
    };
  }, []);

  return {
    skjema,
    id,
    tilstand,
    lagretKl,
    konflikt,
    feiltekst,
    /** Ulagret tekst i minnet — det skjemaet sier når nettet er borte. */
    ulagret: () => r.current.endring !== r.current.lagretEndring,
    /** Versjonen tjeneren sist ga — `baseVersion` for «Bruk forslaget» (H3.3), som lagrer hos tjeneren. */
    versjon: () => r.current.versjon,
    oppdater,
    lagreNaa,
    last,
    nullstill,
    brukLagret,
    beholdDenne,
  };
}
